// =============================================================================
// FingerprintJS open-source — anti-multiaccount.
//
// Принципы:
//   • Open-source (~90% accuracy) — без API key, без vendor account.
//     Pro plan ($200/мес) даёт 99.5% — пока не нужно для MVP.
//   • Считаем visitorId один раз при mount, кешируем в localStorage чтобы
//     не пересчитывать на каждом запросе (≈100ms на init = noticeable).
//   • Отправляем в API через `X-Device-Fingerprint` header при auth-запросах.
//   • Сервер записывает в users.fingerprint_hash при FIRST request юзера и
//     проверяет коллизии: если другой not-flagged user с тем же hash → flag.
//
// Privacy: visitorId ≈ stable hardware/browser fingerprint (canvas, audio,
// fonts, screen). Это PII — не отправляем в Sentry/PostHog. В Sentry уже
// добавили /[0-9a-f]{32}/ в beforeSend? — НЕТ, fingerprint имеет другой формат
// (alphanumeric ~22 chars). Достаточно того, что он только в одном header.
// =============================================================================

const STORAGE_KEY = 'prognoz_fp_v1';

let cachedPromise: Promise<string | null> | null = null;

/**
 * Возвращает stable visitorId (cached в localStorage). Первый вызов ~100ms
 * (на slow devices до 300ms), последующие — мгновенные.
 *
 * Возвращает null если что-то пошло не так — server gracefully fallback'ает
 * на работу без fingerprint check'а (юзер не должен страдать).
 */
export function getDeviceFingerprint(): Promise<string | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);

  // Cached в pам'яті процесса.
  if (cachedPromise) return cachedPromise;

  // Cached в localStorage — TG WebApp перезагружает Mini App при каждом
  // open'е, без localStorage пришлось бы пересчитывать каждый раз.
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && /^[A-Za-z0-9]{16,64}$/.test(stored)) {
      cachedPromise = Promise.resolve(stored);
      return cachedPromise;
    }
  } catch {
    // localStorage может быть недоступен в private mode — продолжаем без cache.
  }

  cachedPromise = (async () => {
    try {
      // Dynamic import — bundle ~30KB gzipped. Не грузим в initial chunk.
      const FingerprintJS = (await import('@fingerprintjs/fingerprintjs')).default;
      const fp = await FingerprintJS.load();
      const r = await fp.get();
      try {
        localStorage.setItem(STORAGE_KEY, r.visitorId);
      } catch {
        /* private mode */
      }
      return r.visitorId;
    } catch {
      return null;
    }
  })();

  return cachedPromise;
}
